import { useState } from 'react'
import { StoredProfile, updatePushSettings } from '@/utils/storage'
import { registerPush, unregisterPush, isPushAvailable, PushApiError } from '@/utils/pushApi'
import { texts } from '@/texts'

interface NotificationSettingsProps {
  profile: StoredProfile
  onUpdate: (profile: StoredProfile) => void
}

export function NotificationSettings({ profile, onUpdate }: NotificationSettingsProps) {
  const [enabled, setEnabled] = useState(profile.pushEnabled ?? false)
  const [hour, setHour] = useState(profile.preferredHourLocal ?? 8)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const available = isPushAvailable()

  const toggle = async () => {
    if (busy) return
    setError(null)
    setBusy(true)
    try {
      if (!enabled) {
        await registerPush({ profile, preferredHourLocal: hour })
        setEnabled(true)
        const updated = updatePushSettings(true, hour)
        if (updated) onUpdate(updated)
      } else {
        await unregisterPush()
        setEnabled(false)
        const updated = updatePushSettings(false)
        if (updated) onUpdate(updated)
      }
    } catch (err) {
      const msg = err instanceof PushApiError ? err.message : texts.errors.unknown
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  const changeHour = async (newHour: number) => {
    setHour(newHour)
    if (!enabled) return
    setError(null)
    setBusy(true)
    try {
      await registerPush({ profile, preferredHourLocal: newHour })
      const updated = updatePushSettings(true, newHour)
      if (updated) onUpdate(updated)
    } catch (err) {
      const msg = err instanceof PushApiError ? err.message : texts.errors.unknown
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mg-card">
      <div className="mg-toggle-row">
        <div style={{ flex: 1 }}>
          <h3 className="tt">{texts.notificationSettings.title}</h3>
          <p className="td">{texts.notificationSettings.description}</p>
        </div>

        <button
          onClick={toggle}
          disabled={busy || !available}
          aria-pressed={enabled}
          className={`mg-switch${enabled ? '' : ' off'}`}
        >
          <span className="mg-knob" />
        </button>
      </div>

      {enabled && (
        <div style={{ marginTop: 'var(--spacing-3)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
          <label
            htmlFor="zahlio-push-hour"
            style={{
              fontSize: '0.9rem',
              color: 'var(--mg-text)',
              fontWeight: 'var(--font-weight-semibold)',
            }}
          >
            {texts.notificationSettings.hourLabel}
          </label>
          <select
            id="zahlio-push-hour"
            value={hour}
            onChange={(e) => changeHour(parseInt(e.target.value, 10))}
            disabled={busy}
            style={{ fontSize: '0.9rem', padding: '0.35rem 0.5rem' }}
          >
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>
                {String(i).padStart(2, '0')}:00
              </option>
            ))}
          </select>
          <span style={{ fontSize: '0.8rem', color: 'var(--mg-text-dim)' }}>{texts.notificationSettings.localTimeHint}</span>
        </div>
      )}

      {!available && (
        <p style={{ marginTop: 'var(--spacing-2)', fontSize: '0.8rem', color: 'var(--mg-text-dim)' }}>
          {texts.notificationSettings.unavailableHint}
        </p>
      )}

      {error && (
        <p style={{ marginTop: 'var(--spacing-2)', fontSize: '0.85rem', color: '#ff8a8a' }}>
          {error}
        </p>
      )}
    </div>
  )
}
